import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import LoadingButton from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import {
  PACKAGE_FIELD_TYPES,
  emptyPackageCustomField,
  emptyPackageEntitlement,
  packageFormFromDetail,
} from '../config/servicePackageFields';
import formatMoney from '../utils/formatMoney';
import { appAlert, appConfirm } from '../utils/appDialog';
import {
  applyEntitlementRateChange,
  applyRateFormChange,
  deriveEntitlementRates,
  deriveRatesFromForm,
  rollupEntitlementRates,
  billRateFromPackage,
  isEntitlementBillAligned,
} from '../utils/packageRates';
import './Services.css';

const money = formatMoney;

function roundMoneySafe(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const EMPTY_PACKAGE_FORM = {
  company_id: '',
  name: '',
  description: '',
  package_price: 0,
  consultant_hourly_rate: 0,
  consultant_bill_rate: 0,
  gross_margin_pct: 0,
  hire_bonus_amount: 0,
  currency: 'USD',
  service_duration: '',
  service_duration_unit: 'minute',
  duration_period: 1,
  duration_period_unit: 'month',
  items: [],
  custom_fields: [],
  _rateLock: 'pay',
};

const CATALOG_RETURN = '/services?tab=catalog';

const ServicePackageDetails = () => {
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('service_manage');
  const isNew = !packageId || packageId === 'new';

  const [packageForm, setPackageForm] = useState(EMPTY_PACKAGE_FORM);
  const [ready, setReady] = useState(isNew);
  const hydratedNewRef = React.useRef(false);

  usePageTitle(
    isNew
      ? 'New service package'
      : packageForm.name
        ? packageForm.name
        : 'Service package'
  );

  const { data: companies = [], isLoading: loadingCompanies } = useQuery(
    'service-companies',
    () => api.get('/services/companies').then((r) => r.data)
  );

  const { data: serviceCatalogCodes = [] } = useQuery(
    'service-catalog-codes',
    () => api.get('/service-catalog-codes').then((r) => r.data)
  );
  const { data: serviceCatalogUnits = [] } = useQuery(
    'service-catalog-units',
    () => api.get('/service-catalog-units').then((r) => r.data)
  );
  const { data: currencies = [] } = useQuery(
    'currencies-active',
    () => api.get('/currencies').then((r) => r.data),
    { staleTime: 60_000 }
  );

  const {
    data: packageDetail,
    isLoading: loadingDetail,
    isError: detailError,
  } = useQuery(
    ['service-package', packageId],
    () => api.get(`/services/packages/${packageId}`).then((r) => r.data),
    { enabled: !isNew && Boolean(packageId) }
  );

  useEffect(() => {
    if (!isNew) {
      hydratedNewRef.current = false;
      return;
    }
    // Only seed the new form once so template/user edits are not wiped when companies load.
    if (hydratedNewRef.current) {
      setPackageForm((prev) => {
        if (prev.company_id) return prev;
        const companyFromQuery = searchParams.get('company_id') || companies[0]?.id || '';
        if (!companyFromQuery) return prev;
        const brand = (companies || []).find((c) => String(c.id) === String(companyFromQuery));
        const base = brand?.erp_base_currency;
        return {
          ...prev,
          company_id: String(companyFromQuery),
          ...(base ? { currency: base } : {}),
        };
      });
      return;
    }
    const companyFromQuery = searchParams.get('company_id') || companies[0]?.id || '';
    const brand = companyFromQuery
      ? (companies || []).find((c) => String(c.id) === String(companyFromQuery))
      : null;
    const base = brand?.erp_base_currency;
    setPackageForm({
      ...EMPTY_PACKAGE_FORM,
      company_id: companyFromQuery ? String(companyFromQuery) : '',
      ...(base ? { currency: base } : {}),
    });
    hydratedNewRef.current = true;
    setReady(true);
  }, [isNew, searchParams, companies]);

  useEffect(() => {
    if (isNew) return;
    if (packageDetail) {
      setPackageForm(packageFormFromDetail(packageDetail));
      setReady(true);
    }
  }, [isNew, packageDetail]);

  const goBack = () => navigate(CATALOG_RETURN);

  const createPackageMutation = useMutation(
    (payload) => api.post('/services/packages', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('service-packages');
        const id = res?.data?.id;
        if (id) {
          navigate(`/services/packages/${id}`, { replace: true });
        } else {
          goBack();
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updatePackageMutation = useMutation(
    ({ id, payload }) => api.put(`/services/packages/${id}`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-packages');
        queryClient.invalidateQueries(['service-package', packageId]);
        void appAlert('Package saved.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deletePackageMutation = useMutation(
    (id) => api.delete(`/services/packages/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-packages');
        goBack();
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updatePackageItem = (idx, patch) => {
    setPackageForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };

  const updatePackageItemRate = (idx, field, value) => {
    setPackageForm((prev) => {
      const items = [...prev.items];
      items[idx] = applyEntitlementRateChange(items[idx], field, value);
      return { ...prev, items };
    });
  };

  const updatePackageRateField = (field, value) => {
    setPackageForm((prev) => applyRateFormChange(prev, field, value));
  };

  const addPackageCustomField = () => {
    setPackageForm((prev) => ({
      ...prev,
      custom_fields: [...prev.custom_fields, emptyPackageCustomField()],
    }));
  };

  const addCustomEntitlementRow = () => {
    setPackageForm((prev) => {
      const packageBill = billRateFromPackage(
        prev.package_price,
        prev.service_duration,
        prev.service_duration_unit
      );
      const row = {
        ...emptyPackageEntitlement(),
        unit: prev.service_duration_unit || 'minute',
        bill_rate: packageBill != null ? packageBill : 0,
        pay_rate: 0,
      };
      return { ...prev, items: [...prev.items, row] };
    });
  };

  const removePackageItem = (idx) => {
    setPackageForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  const mentorshipTemplate = () => {
    const byCode = Object.fromEntries((serviceCatalogCodes || []).map((c) => [c.code, c]));
    const unitFor = (code, fallback) => {
      const preferred = {
        interview: 'session',
        resume_edit: 'edit',
        consultant_minute: 'minute',
        hire_bonus: 'flat',
      }[code];
      return preferred || fallback || serviceCatalogUnits[0]?.code || 'unit';
    };
    const ratesBase = applyRateFormChange(
      {
        ...EMPTY_PACKAGE_FORM,
        package_price: 2500,
        service_duration: 260,
        service_duration_unit: unitFor('consultant_minute') || 'minute',
        consultant_hourly_rate: 0,
        consultant_bill_rate: 0,
        gross_margin_pct: 0,
        items: [],
      },
      'package_price',
      2500
    );
    const packageBill = ratesBase.consultant_bill_rate || 0;
    setPackageForm((prev) => ({
      ...prev,
      ...ratesBase,
      company_id: prev.company_id,
      name: 'Mentorship Program',
      description:
        '3 mock interviews, 3 resume revisions, 260 minutes of consultant time, plus hire bonus.',
      package_price: 2500,
      service_duration: 260,
      service_duration_unit: ratesBase.service_duration_unit,
      duration_period: 1,
      duration_period_unit: 'month',
      hire_bonus_amount: 750,
      items: [
        applyEntitlementRateChange(
          {
            ...emptyPackageEntitlement(),
            code: 'interview',
            name: byCode.interview?.name || 'Mock interview',
            unit: unitFor('interview') || 'session',
            quantity: 3,
            bill_rate: 200,
            pay_rate: 100,
          },
          'pay_rate',
          100
        ),
        applyEntitlementRateChange(
          {
            ...emptyPackageEntitlement(),
            code: 'resume_edit',
            name: byCode.resume_edit?.name || 'Resume revision',
            unit: unitFor('resume_edit') || 'edit',
            quantity: 3,
            bill_rate: 150,
            pay_rate: 75,
          },
          'pay_rate',
          75
        ),
        applyEntitlementRateChange(
          {
            ...emptyPackageEntitlement(),
            code: 'consultant_minute',
            name: byCode.consultant_minute?.name || 'Consultant time',
            unit: unitFor('consultant_minute') || 'minute',
            quantity: 260,
            bill_rate: packageBill,
            pay_rate: Math.round(packageBill * 0.75 * 100) / 100,
          },
          'pay_rate',
          Math.round(packageBill * 0.75 * 100) / 100
        ),
        applyEntitlementRateChange(
          {
            ...emptyPackageEntitlement(),
            code: 'hire_bonus',
            name: byCode.hire_bonus?.name || 'Placement bonus',
            unit: unitFor('hire_bonus') || 'flat',
            billing_mode: 'bonus',
            quantity: 1,
            bill_rate: 150,
            pay_rate: 150,
          },
          'pay_rate',
          150
        ),
      ],
      custom_fields: [
        emptyPackageCustomField(),
        { label: 'Program duration', field_type: 'text', value: '12 weeks', show_on_card: true },
      ],
    }));
  };

  const submitPackage = (e) => {
    e.preventDefault();
    if (!canManage) return;
    const itemsPayload = packageForm.items
      .filter((i) => String(i.code || '').trim() && Number(i.quantity) > 0)
      .map((i) => {
        const rates = deriveEntitlementRates(i);
        return {
          catalog_item_id: i.catalog_item_id || null,
          quantity: Number(i.quantity) || 0,
          notes: i.notes?.trim() || null,
          custom_label: i.custom_label?.trim() || i.name?.trim() || null,
          pay_rate: Number(rates.pay_rate) || 0,
          bill_rate: Number(i.bill_rate) || rates.bill_rate || 0,
          gross_margin_pct: Number(i.gross_margin_pct) || 0,
          burden_mode: i.burden_mode === 'amount' ? 'amount' : 'pct',
          burden_value: Number(i.burden_value) || 0,
          overhead_mode: i.overhead_mode === 'amount' ? 'amount' : 'pct',
          overhead_value: Number(i.overhead_value) || 0,
          line_price: rates.total_bill || 0,
          total_bill: rates.total_bill || 0,
          new_catalog: {
            code: String(i.code).trim(),
            name: String(i.name || i.custom_label || i.code).trim(),
            unit: String(i.unit || 'unit').trim(),
            billing_mode: i.billing_mode || 'usage',
            unit_price: Number(i.bill_rate) || Number(i.unit_price) || 0,
            description: i.notes?.trim() || null,
          },
        };
      });
    const rollup = rollupEntitlementRates(packageForm.items);
    const pkgRates = deriveRatesFromForm(packageForm);
    // Package pay for storage: first duration-aligned entitlement's employee pay
    const timeLine = packageForm.items.find((i) =>
      isEntitlementBillAligned(i.unit, packageForm.service_duration_unit)
    );
    const packagePay = Number(timeLine?.pay_rate) || 0;
    const packageBill = Number(pkgRates.bill_rate) || 0;
    const packageGm =
      packageBill > 0 && packagePay >= 0
        ? Math.round(((packageBill - packagePay) / packageBill) * 10000) / 100
        : Number(pkgRates.gross_margin_pct) || 0;
    const payload = {
      ...packageForm,
      company_id: Number(packageForm.company_id),
      package_price: Number(packageForm.package_price) || rollup.total_bill || 0,
      consultant_hourly_rate: packagePay,
      consultant_pay_rate: packagePay,
      consultant_bill_rate: packageBill,
      gross_margin_pct: packageGm,
      hire_bonus_amount: Number(packageForm.hire_bonus_amount) || 0,
      service_duration:
        packageForm.service_duration === '' || packageForm.service_duration == null
          ? null
          : Number(packageForm.service_duration),
      service_duration_unit: packageForm.service_duration_unit || null,
      duration_period:
        packageForm.duration_period === '' || packageForm.duration_period == null
          ? null
          : Number(packageForm.duration_period),
      duration_period_unit: packageForm.duration_period_unit || null,
      items: itemsPayload,
      custom_fields: packageForm.custom_fields
        .filter((f) => f.label?.trim())
        .map((f, index) => ({
          label: f.label.trim(),
          field_type: f.field_type || 'text',
          value:
            f.field_type === 'boolean'
              ? f.value === true || f.value === 'true' || f.value === '1'
                ? 'true'
                : 'false'
              : f.value != null
                ? String(f.value)
                : '',
          show_on_card: f.show_on_card !== false,
          sort_order: (index + 1) * 10,
        })),
    };
    if (isNew) {
      createPackageMutation.mutate(payload);
    } else {
      updatePackageMutation.mutate({ id: packageId, payload });
    }
  };

  if (!isNew && (loadingDetail || !ready) && !detailError) {
    return <div className="loading">Loading package…</div>;
  }

  if (!isNew && (detailError || (!loadingDetail && !packageDetail && ready))) {
    return (
      <div className="svc-package-page list-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back to catalog" onClick={goBack} />
        </div>
        <div className="error">Package not found</div>
      </div>
    );
  }

  if (isNew && loadingCompanies && !companies.length) {
    return <div className="loading">Loading…</div>;
  }

  const title = isNew
    ? canManage
      ? 'Create service package'
      : 'Service package'
    : canManage
      ? 'Edit service package'
      : 'View service package';

  return (
    <div className="svc-package-page list-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to catalog" onClick={goBack} />
      </div>

      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <div>
            <h1>{title}</h1>
            {!isNew && packageForm.name ? (
              <p className="svc-package-page-sub">{packageForm.name}</p>
            ) : null}
            {!canManage && !isNew ? (
              <p className="svc-form-hint">
                You can view this package but do not have permission to edit it.
              </p>
            ) : null}
          </div>
        </div>

        <form
          onSubmit={submitPackage}
          className={`svc-package-form${!canManage ? ' is-readonly' : ''}`}
        >
          <fieldset disabled={!canManage} className="svc-package-fieldset">
            <div className="svc-form-grid svc-form-grid--2">
              <div className="form-group">
                <label>Pod</label>
                <select
                  value={packageForm.company_id}
                  onChange={(e) => {
                    const companyId = e.target.value;
                    const brand = companies.find((c) => String(c.id) === String(companyId));
                    const base = brand?.erp_base_currency;
                    setPackageForm({
                      ...packageForm,
                      company_id: companyId,
                      ...(base ? { currency: base } : {}),
                    });
                  }}
                  required
                  disabled={Boolean(!isNew) || !canManage}
                >
                  <option value="">— Select pod —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.erp_base_currency ? ` · ${c.erp_base_currency}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Package name</label>
                <input
                  value={packageForm.name}
                  onChange={(e) =>
                    setPackageForm({ ...packageForm, name: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            {isNew && canManage && (
              <button
                type="button"
                className="btn btn-secondary btn-sm svc-template-btn"
                onClick={mentorshipTemplate}
              >
                Use Mentorship template
              </button>
            )}

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={packageForm.description}
                onChange={(e) =>
                  setPackageForm({ ...packageForm, description: e.target.value })
                }
                rows={2}
              />
            </div>

            <div className="svc-form-grid svc-form-grid--package-meta">
              <div className="form-group form-group--num">
                <label>Package price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={packageForm.package_price}
                  onChange={(e) => updatePackageRateField('package_price', e.target.value)}
                />
              </div>
              <div className="form-group form-group--currency">
                <label>Currency</label>
                <select
                  value={packageForm.currency || 'USD'}
                  onChange={(e) =>
                    setPackageForm({ ...packageForm, currency: e.target.value })
                  }
                >
                  {(currencies.length
                    ? currencies
                    : [{ code: 'USD' }, { code: 'INR' }, { code: 'CAD' }, { code: 'EUR' }]
                  ).map((c) => {
                    const code = c.code || c.currency_code || c;
                    return (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    );
                  })}
                  {packageForm.currency &&
                    !(currencies || []).some(
                      (c) => (c.code || c.currency_code) === packageForm.currency
                    ) &&
                    !['USD', 'INR', 'CAD', 'EUR'].includes(packageForm.currency) && (
                      <option value={packageForm.currency}>{packageForm.currency}</option>
                    )}
                </select>
              </div>
              <div className="form-group form-group--num">
                <label>Service duration</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={packageForm.service_duration}
                  onChange={(e) =>
                    updatePackageRateField('service_duration', e.target.value)
                  }
                  placeholder="e.g. 600"
                  title="How much service is included (for bill rate)"
                />
              </div>
              <div className="form-group form-group--unit">
                <label>Duration unit</label>
                <select
                  value={packageForm.service_duration_unit || ''}
                  onChange={(e) =>
                    updatePackageRateField('service_duration_unit', e.target.value)
                  }
                  title="Unit for included service allotment (e.g. minute)"
                >
                  <option value="">— Select unit —</option>
                  {serviceCatalogUnits.map((u) => (
                    <option key={u.id || u.code} value={u.code}>
                      {u.name} ({u.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group--num">
                <label>Valid period</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={packageForm.duration_period}
                  onChange={(e) =>
                    setPackageForm({ ...packageForm, duration_period: e.target.value })
                  }
                  placeholder="e.g. 1"
                  title="How long the package allotment stays valid"
                />
              </div>
              <div className="form-group form-group--unit">
                <label>Period unit</label>
                <select
                  value={packageForm.duration_period_unit || ''}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      duration_period_unit: e.target.value,
                    })
                  }
                  title="Calendar period for enrollment end date"
                >
                  <option value="">— Select unit —</option>
                  {(serviceCatalogUnits.length
                    ? serviceCatalogUnits
                    : [
                        { code: 'day', name: 'Day' },
                        { code: 'week', name: 'Week' },
                        { code: 'month', name: 'Month' },
                        { code: 'year', name: 'Year' },
                      ]
                  )
                    .filter((u) =>
                      ['day', 'days', 'week', 'weeks', 'month', 'months', 'year', 'years'].includes(
                        String(u.code || '').toLowerCase()
                      )
                    )
                    .map((u) => (
                      <option key={u.id || u.code} value={u.code}>
                        {u.name} ({u.code})
                      </option>
                    ))}
                  {packageForm.duration_period_unit &&
                    !['day', 'days', 'week', 'weeks', 'month', 'months', 'year', 'years'].includes(
                      String(packageForm.duration_period_unit).toLowerCase()
                    ) && (
                      <option value={packageForm.duration_period_unit}>
                        {packageForm.duration_period_unit}
                      </option>
                    )}
                </select>
              </div>
              {(() => {
                const pkgR = deriveRatesFromForm(packageForm);
                const rateLabel =
                  pkgR.rate_label === 'flat'
                    ? 'flat'
                    : pkgR.rate_label
                      ? pkgR.rate_label
                      : '';
                return (
                  <div className="form-group form-group--num form-group--readonly">
                    <label>
                      Bill rate
                      {rateLabel ? ` (${rateLabel})` : ''}
                    </label>
                    <input
                      type="text"
                      readOnly
                      tabIndex={-1}
                      value={pkgR.bill_rate ?? 0}
                      title={
                        pkgR.multi_label
                          ? `Package price ÷ ${pkgR.multi_label}`
                          : 'Package price ÷ duration'
                      }
                    />
                  </div>
                );
              })()}
            </div>
            {(() => {
              const allot =
                packageForm.service_duration && packageForm.service_duration_unit
                  ? `${packageForm.service_duration} ${packageForm.service_duration_unit}`
                  : null;
              const period =
                packageForm.duration_period && packageForm.duration_period_unit
                  ? `${packageForm.duration_period} ${packageForm.duration_period_unit}`
                  : null;
              const pkgR = deriveRatesFromForm(packageForm);
              const cur = packageForm.currency || 'USD';
              if (!allot && !period && !(pkgR.multi > 0 && Number(packageForm.package_price) > 0)) {
                return null;
              }
              return (
                <p className="svc-form-hint svc-package-bill-hint">
                  {allot && period ? (
                    <>
                      Package: <strong>{allot}</strong> valid for <strong>{period}</strong>
                      {' · '}
                    </>
                  ) : period ? (
                    <>
                      Valid for <strong>{period}</strong>
                      {' · '}
                    </>
                  ) : allot ? (
                    <>
                      Allotment: <strong>{allot}</strong>
                      {' · '}
                    </>
                  ) : null}
                  {pkgR.multi > 0 && Number(packageForm.package_price) > 0 ? (
                    <>
                      Bill rate = {money(packageForm.package_price, cur)} ÷{' '}
                      {pkgR.multi_label || 'duration'} → <strong>{pkgR.bill_rate}</strong>
                      {pkgR.rate_label && pkgR.rate_label !== 'flat'
                        ? ` ${pkgR.rate_label}`
                        : ''}
                      .
                    </>
                  ) : null}{' '}
                  Enrollment end date = start + valid period. Entitlements split package cost
                  into services and employee pay.
                </p>
              );
            })()}

            <div className="svc-form-section-head">
              <h3>Included entitlements</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addCustomEntitlementRow}
              >
                <FiPlus /> Add entitlement
              </button>
            </div>

            {packageForm.items.length > 0 ? (
              (() => {
                const roll = rollupEntitlementRates(packageForm.items);
                const cur = packageForm.currency || 'USD';
                return (
                  <div className="svc-rate-summary svc-rate-summary--package" aria-live="polite">
                    <div className="svc-rate-summary-item">
                      <span className="svc-rate-summary-label">Σ Bill</span>
                      <strong>{money(roll.total_bill, cur)}</strong>
                    </div>
                    <div className="svc-rate-summary-item">
                      <span className="svc-rate-summary-label">Σ Spread</span>
                      <strong className={roll.total_spread >= 0 ? 'is-profit' : 'is-loss'}>
                        {money(roll.total_spread, cur)}
                      </strong>
                    </div>
                    <div className="svc-rate-summary-item">
                      <span className="svc-rate-summary-label">Σ Burden</span>
                      <strong>{money(roll.total_burden || 0, cur)}</strong>
                    </div>
                    <div className="svc-rate-summary-item">
                      <span className="svc-rate-summary-label">Σ OpEx</span>
                      <strong>{money(roll.total_overhead || 0, cur)}</strong>
                    </div>
                    <div className="svc-rate-summary-item">
                      <span className="svc-rate-summary-label">Σ Gross P&amp;L</span>
                      <strong
                        className={
                          (roll.total_contribution ?? 0) >= 0 ? 'is-profit' : 'is-loss'
                        }
                      >
                        {money(roll.total_contribution ?? 0, cur)}
                      </strong>
                    </div>
                  </div>
                );
              })()
            ) : null}

            <div className="svc-package-items-form">
              {packageForm.items.length === 0 ? (
                <p className="svc-empty">
                  No entitlements yet. Click “Add entitlement” to include services.
                </p>
              ) : null}
              {packageForm.items.map((item, idx) => {
                const r = deriveEntitlementRates(item);
                const cur = packageForm.currency || 'USD';
                const billLocked = isEntitlementBillAligned(
                  item.unit,
                  packageForm.service_duration_unit
                );
                const spreadUnit = Number(r.spread) || 0;
                const burdenAmt = Number(r.burden_rate ?? r.total_burden) || 0;
                const opexAmt = Number(r.overhead_rate ?? r.total_overhead) || 0;
                const grossPl =
                  r.contribution_rate != null
                    ? Number(r.contribution_rate)
                    : roundMoneySafe(spreadUnit - burdenAmt - opexAmt);
                const grossPlPct =
                  r.contribution_margin_pct != null
                    ? r.contribution_margin_pct
                    : Number(r.bill_rate) > 0
                      ? Math.round((grossPl / Number(r.bill_rate)) * 10000) / 100
                      : 0;

                const onUnitChange = (unit) => {
                  const packageBill = billRateFromPackage(
                    packageForm.package_price,
                    packageForm.service_duration,
                    packageForm.service_duration_unit
                  );
                  setPackageForm((prev) => {
                    let row = applyEntitlementRateChange(prev.items[idx], 'unit', unit);
                    if (
                      packageBill != null &&
                      isEntitlementBillAligned(unit, prev.service_duration_unit)
                    ) {
                      row = applyEntitlementRateChange(row, 'bill_rate', packageBill);
                    }
                    const items = [...prev.items];
                    items[idx] = row;
                    return { ...prev, items };
                  });
                };

                const unitSelect = (
                  <select
                    value={item.unit || ''}
                    onChange={(e) => onUnitChange(e.target.value)}
                    required
                    aria-label="Unit"
                  >
                    <option value="">—</option>
                    {serviceCatalogUnits.map((u) => (
                      <option key={u.id} value={u.code}>
                        {u.name ? `${u.name} (${u.code})` : u.code}
                      </option>
                    ))}
                    {item.unit &&
                      !serviceCatalogUnits.some((u) => u.code === item.unit) && (
                        <option value={item.unit}>{item.unit}</option>
                      )}
                  </select>
                );

                return (
                  <div
                    key={item.catalog_item_id ? `cat-${item.catalog_item_id}` : `new-${idx}`}
                    className="svc-package-item-row svc-ent-formula"
                  >
                    <div className="svc-ent-line svc-ent-line--meta">
                      <div className="svc-ent-field svc-ent-field--service">
                        <span className="svc-ent-caption">Service</span>
                        <select
                          value={item.code || ''}
                          onChange={(e) => {
                            const code = e.target.value;
                            const meta = serviceCatalogCodes.find((c) => c.code === code);
                            updatePackageItem(idx, {
                              code,
                              name: meta?.name || item.name || '',
                              unit:
                                item.unit ||
                                meta?.unit ||
                                packageForm.service_duration_unit ||
                                'unit',
                            });
                          }}
                          required
                          aria-label="Service"
                        >
                          <option value="">— Select —</option>
                          {serviceCatalogCodes.map((c) => (
                            <option key={c.id} value={c.code}>
                              {c.code} — {c.name}
                            </option>
                          ))}
                          {item.code &&
                            !serviceCatalogCodes.some((c) => c.code === item.code) && (
                              <option value={item.code}>{item.code}</option>
                            )}
                        </select>
                      </div>
                      <div className="svc-ent-field svc-ent-field--qty">
                        <span className="svc-ent-caption">Qty</span>
                        <input
                          type="number"
                          min="0"
                          value={item.quantity}
                          onChange={(e) =>
                            updatePackageItemRate(idx, 'quantity', e.target.value)
                          }
                          aria-label="Quantity"
                        />
                      </div>
                      <div className="svc-ent-field svc-ent-field--unit">
                        <span className="svc-ent-caption">Unit</span>
                        {unitSelect}
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm svc-ent-remove"
                        onClick={() => removePackageItem(idx)}
                        aria-label="Remove entitlement"
                      >
                        <FiTrash2 />
                      </button>
                    </div>

                    <div
                      className="svc-ent-line svc-ent-line--eq2"
                      role="group"
                      aria-label="Bill rate minus pay rate equals spread"
                    >
                      <div
                        className={`svc-ent-field${billLocked ? ' is-locked' : ''}`}
                      >
                        <span className="svc-ent-caption">Bill rate</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          readOnly={billLocked}
                          tabIndex={billLocked ? -1 : 0}
                          value={item.bill_rate ?? 0}
                          onChange={(e) =>
                            updatePackageItemRate(idx, 'bill_rate', e.target.value)
                          }
                          title={
                            billLocked
                              ? 'Follows package bill rate'
                              : 'Client rate for this service'
                          }
                          aria-label="Bill rate"
                        />
                      </div>
                      <span className="svc-ent-op" aria-hidden="true">
                        −
                      </span>
                      <div className="svc-ent-field">
                        <span className="svc-ent-caption">Pay rate</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.pay_rate ?? 0}
                          onChange={(e) =>
                            updatePackageItemRate(idx, 'pay_rate', e.target.value)
                          }
                          title="Employee / consultant cost"
                          aria-label="Pay rate"
                        />
                      </div>
                      <span className="svc-ent-op" aria-hidden="true">
                        =
                      </span>
                      <div className="svc-ent-field svc-ent-field--result">
                        <span className="svc-ent-caption">Spread</span>
                        <output
                          className={`svc-ent-value${
                            spreadUnit >= 0 ? ' is-profit' : ' is-loss'
                          }`}
                          aria-label="Spread"
                        >
                          {money(spreadUnit, cur)}
                        </output>
                      </div>
                    </div>

                    <div
                      className="svc-ent-line svc-ent-line--eq3"
                      role="group"
                      aria-label="Spread minus burden and OpEx equals gross P and L"
                    >
                      <div className="svc-ent-field svc-ent-field--result">
                        <span className="svc-ent-caption">Spread</span>
                        <output
                          className={`svc-ent-value${
                            spreadUnit >= 0 ? ' is-profit' : ' is-loss'
                          }`}
                          aria-hidden="true"
                        >
                          {money(spreadUnit, cur)}
                        </output>
                      </div>
                      <span className="svc-ent-op" aria-hidden="true">
                        −
                      </span>
                      <span className="svc-ent-op svc-ent-op--paren" aria-hidden="true">
                        (
                      </span>
                      <div className="svc-ent-field svc-ent-field--cost">
                        <span className="svc-ent-caption">Burden</span>
                        <div className="svc-ent-cost">
                          <select
                            value={item.burden_mode === 'amount' ? 'amount' : 'pct'}
                            onChange={(e) =>
                              updatePackageItemRate(idx, 'burden_mode', e.target.value)
                            }
                            aria-label="Burden mode"
                          >
                            <option value="pct">%</option>
                            <option value="amount">$</option>
                          </select>
                          <input
                            type="number"
                            min="0"
                            max={item.burden_mode === 'amount' ? undefined : 100}
                            step="0.01"
                            value={item.burden_value ?? 0}
                            onChange={(e) =>
                              updatePackageItemRate(idx, 'burden_value', e.target.value)
                            }
                            aria-label="Burden value"
                          />
                        </div>
                      </div>
                      <span className="svc-ent-op" aria-hidden="true">
                        +
                      </span>
                      <div className="svc-ent-field svc-ent-field--cost">
                        <span className="svc-ent-caption">OpEx</span>
                        <div className="svc-ent-cost">
                          <select
                            value={item.overhead_mode === 'amount' ? 'amount' : 'pct'}
                            onChange={(e) =>
                              updatePackageItemRate(idx, 'overhead_mode', e.target.value)
                            }
                            aria-label="OpEx mode"
                          >
                            <option value="pct">%</option>
                            <option value="amount">$</option>
                          </select>
                          <input
                            type="number"
                            min="0"
                            max={item.overhead_mode === 'amount' ? undefined : 100}
                            step="0.01"
                            value={item.overhead_value ?? 0}
                            onChange={(e) =>
                              updatePackageItemRate(idx, 'overhead_value', e.target.value)
                            }
                            aria-label="OpEx value"
                          />
                        </div>
                      </div>
                      <span className="svc-ent-op svc-ent-op--paren" aria-hidden="true">
                        )
                      </span>
                      <span className="svc-ent-op" aria-hidden="true">
                        =
                      </span>
                      <div className="svc-ent-field svc-ent-field--result">
                        <span className="svc-ent-caption">Gross P&amp;L</span>
                        <output
                          className={`svc-ent-value${
                            grossPl >= 0 ? ' is-profit' : ' is-loss'
                          }`}
                          aria-label="Gross P and L"
                        >
                          {money(grossPl, cur)}
                        </output>
                      </div>
                      <div className="svc-ent-field svc-ent-field--result svc-ent-field--pct">
                        <span className="svc-ent-caption">Gross %</span>
                        <output
                          className={`svc-ent-value${
                            grossPlPct >= 0 ? ' is-profit' : ' is-loss'
                          }`}
                          aria-label="Gross percent"
                        >
                          {grossPlPct}%
                        </output>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="svc-form-section-head">
              <h3>Additional package fields</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addPackageCustomField}
              >
                <FiPlus /> Add field
              </button>
            </div>
            <div className="svc-custom-fields-form">
              {packageForm.custom_fields.length === 0 ? (
                <p className="svc-empty">No custom fields yet.</p>
              ) : null}
              {packageForm.custom_fields.map((field, idx) => (
                <div key={`field-${idx}`} className="svc-custom-field-row">
                  <div className="svc-form-grid svc-form-grid--fields">
                    <div className="form-group">
                      <label>Label</label>
                      <input
                        value={field.label}
                        onChange={(e) => {
                          const custom_fields = [...packageForm.custom_fields];
                          custom_fields[idx] = { ...custom_fields[idx], label: e.target.value };
                          setPackageForm({ ...packageForm, custom_fields });
                        }}
                        placeholder="Program duration"
                      />
                    </div>
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        value={field.field_type}
                        onChange={(e) => {
                          const custom_fields = [...packageForm.custom_fields];
                          custom_fields[idx] = {
                            ...custom_fields[idx],
                            field_type: e.target.value,
                            value: '',
                          };
                          setPackageForm({ ...packageForm, custom_fields });
                        }}
                      >
                        {PACKAGE_FIELD_TYPES.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Value</label>
                      {field.field_type === 'boolean' ? (
                        <select
                          value={
                            field.value === true || field.value === 'true' ? 'true' : 'false'
                          }
                          onChange={(e) => {
                            const custom_fields = [...packageForm.custom_fields];
                            custom_fields[idx] = {
                              ...custom_fields[idx],
                              value: e.target.value,
                            };
                            setPackageForm({ ...packageForm, custom_fields });
                          }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : field.field_type === 'textarea' ? (
                        <textarea
                          rows={2}
                          value={field.value}
                          onChange={(e) => {
                            const custom_fields = [...packageForm.custom_fields];
                            custom_fields[idx] = {
                              ...custom_fields[idx],
                              value: e.target.value,
                            };
                            setPackageForm({ ...packageForm, custom_fields });
                          }}
                        />
                      ) : (
                        <input
                          type={
                            field.field_type === 'number' || field.field_type === 'currency'
                              ? 'number'
                              : 'text'
                          }
                          value={field.value}
                          onChange={(e) => {
                            const custom_fields = [...packageForm.custom_fields];
                            custom_fields[idx] = {
                              ...custom_fields[idx],
                              value: e.target.value,
                            };
                            setPackageForm({ ...packageForm, custom_fields });
                          }}
                        />
                      )}
                    </div>
                    <div className="svc-field-actions">
                      <label className="svc-checkbox-label">
                        <input
                          type="checkbox"
                          checked={field.show_on_card !== false}
                          onChange={(e) => {
                            const custom_fields = [...packageForm.custom_fields];
                            custom_fields[idx] = {
                              ...custom_fields[idx],
                              show_on_card: e.target.checked,
                            };
                            setPackageForm({ ...packageForm, custom_fields });
                          }}
                        />
                        Show on card
                      </label>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm svc-remove-row"
                        onClick={() => {
                          setPackageForm({
                            ...packageForm,
                            custom_fields: packageForm.custom_fields.filter(
                              (_, i) => i !== idx
                            ),
                          });
                        }}
                        aria-label="Remove field"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </fieldset>

          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={goBack}>
              Back to catalog
            </button>
            {canManage && !isNew ? (
              <LoadingButton
                type="button"
                className="btn btn-secondary"
                loading={deletePackageMutation.isLoading}
                loadingLabel="Deleting…"
                onClick={async () => {
                  if (
                    !(await appConfirm(
                      `Archive package “${packageForm.name || 'this package'}”? This is a soft delete (catalog only). Historical enrollments and payouts keep their link to this package.`
                    ))
                  ) {
                    return;
                  }
                  deletePackageMutation.mutate(packageId);
                }}
              >
                <FiTrash2 /> Archive
              </LoadingButton>
            ) : null}
            {canManage ? (
              <LoadingButton
                type="submit"
                className="btn btn-primary"
                loading={createPackageMutation.isLoading || updatePackageMutation.isLoading}
              >
                {isNew ? 'Create package' : 'Save package'}
              </LoadingButton>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServicePackageDetails;
